import React from 'react';
import Utilities from '@util/Utilities';
import AlertUtilities from '@app/site/secured/rateshopper/rates/alert/util/AlertUtilities';
import AlertExecution from '@app/model/alert/AlertExecution';
import PriceComparisonAlertExecutionNotificationOutput
	from '@app/model/alert/output/PriceComparisonAlertExecutionNotificationOutput';
import PriceComparisonAlertRulePricePoint from '@app/model/alert/PriceComparisonAlertRulePricePoint';


const PriceComparisonAlertExecutionNotificationRowFragment: React.FunctionComponent<{
	alertExecution: AlertExecution,
	notificationOutput: PriceComparisonAlertExecutionNotificationOutput,
	marketStateDetailsModal: any,
	showPreviousRun?: boolean,
	isInDrawer?: boolean
}>
	= ({ alertExecution, notificationOutput, marketStateDetailsModal, showPreviousRun = false, isInDrawer = false }) => {

	const findCompetitor = (competitorId: number) => {
		return alertExecution.alertOutput.competitors!.find(c => c.id === competitorId);
	};

	const createPriceChangeIndicator = (currentAmount: number, previousAmount: number) => {
		if (previousAmount === currentAmount) return <span />;
		return currentAmount > previousAmount ? <span className="ml-1 text-xs"><span className="text-green-600">▲</span><span className="text-gray-400">({currentAmount - previousAmount})</span></span>
			: <span className="ml-1 text-xs"><span className="text-red-600">▼</span><span className="text-gray-400">({previousAmount - currentAmount})</span></span>;
	};

	const showPriceChangeIndicator = (output: PriceComparisonAlertExecutionNotificationOutput) => {
		return output.previous
			? createPriceChangeIndicator(output.current.price.amount, output.previous.price.amount)
			: <div />;
	};

	const showCompetitorsPriceChangeIndicator = (output: PriceComparisonAlertExecutionNotificationOutput) => {
		return output.previous
			? createPriceChangeIndicator(output.current.competitorsPrice.amount, output.previous.competitorsPrice.amount)
			: <div />;
	};

	return <tr>
		<td className="whitespace-nowrap py-4 pl-0 pr-3 text-sm font-semibold text-gray-950">
			{Utilities.formatDateString(notificationOutput.current.statDate, 'ddd DD MMM', false)}
			{!showPreviousRun && notificationOutput.isNew &&
				<span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">New</span>}
			{!showPreviousRun && notificationOutput.isUpdated &&
				<span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">Updated</span>}
		</td>
		<td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
			{Utilities.formatDouble(notificationOutput.current.price.amount, 0)}
			{showPreviousRun && showPriceChangeIndicator(notificationOutput)}
		</td>
		<td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
			{Utilities.formatDouble(notificationOutput.current.competitorsPrice.amount, 0)}
			{showPreviousRun && showCompetitorsPriceChangeIndicator(notificationOutput)}
		</td>
		{alertExecution.alertOutput.priceComparisonAlertRule.pricePoint !== PriceComparisonAlertRulePricePoint.MEDIAN &&
			<td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
				{findCompetitor(notificationOutput.current.competitorId)?.name}
			</td>}
		{!showPreviousRun && <td className="whitespace-nowrap px-2 py-4 text-sm">
			<FormatDifferencePercentage percentage={AlertUtilities.calculateDifference(notificationOutput.current)} />
		</td>}
		{showPreviousRun && !!notificationOutput.previous && <td className="whitespace-nowrap px-2 py-4 text-sm">
			<FormatDifferencePercentage percentage={notificationOutput.previousRate * 100} />
			<span className="mx-2 text-gray-300">→</span>
			<FormatDifferencePercentage percentage={notificationOutput.currentRate * 100} />
		</td>}
		{showPreviousRun && !notificationOutput.previous && <td className="whitespace-nowrap px-2 py-4 text-sm">
			<FormatDifferencePercentage percentage={AlertUtilities.calculateDifference(notificationOutput.current)} />
		</td>}
		{!showPreviousRun && !isInDrawer &&
			<td className="relative whitespace-nowrap py-4 pl-3 pr-0 text-right text-sm font-semibold">
				<a href="#"
				   className="text-blue-600 hover:text-blue-800"
				   onClick={() => marketStateDetailsModal.current!.open(notificationOutput!.current.alertId, notificationOutput.current.marketStateId)}>Details</a>
			</td>}
	</tr>;
};

const FormatDifferencePercentage: React.FunctionComponent<{ percentage: number }> = ({ percentage }) => {
	const rounded = parseInt(Utilities.formatDouble(percentage, 0));
	return rounded > 0
		? <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/10">+{rounded}%</span>
		: <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-600/10">{rounded}%</span>;
};

export default PriceComparisonAlertExecutionNotificationRowFragment;
